import { Injectable } from '@angular/core';
import { debounceTime, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginService } from '../../login/login.service';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private readonly activitySubject = new Subject<void>();
  /**
   * Holds the JavaScript timeout handle by its numeric id
   * @private
   */
  private activityTimeout: ReturnType<typeof setTimeout> | undefined =
    undefined;

  constructor(private readonly loginService: LoginService) {
    // Start the initial timeout when service is created
    this.activitySubject
      .pipe(
        // prevent excessive calls by only resetting the timer at most once every half second
        debounceTime(500),
      )
      .subscribe(() => {
        this.resetActivityTimer();
      });
  }

  /**
   * Call this method when any user activity is detected
   */
  onUserActivity(): void {
    this.activitySubject.next();
  }

  /**
   * Initialize the session timeout monitoring
   */
  initializeSessionTimeout(): void {
    this.activitySubject.next();
  }

  private resetActivityTimer() {
    // Clear existing timeout
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }

    // Set new timeout for inactivity detection
    this.activityTimeout = setTimeout(
      () => {
        console.error(
          'Session timeout reached, logging out user due to inactivity',
        );
        this.logoutUser();
      },
      environment.session_timeout_minutes * 60 * 1000,
    ); // Convert minutes to milliseconds
  }

  private async logoutUser(): Promise<void> {
    try {
      await this.loginService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue execution even if logout fails
    }
  }

  /**
   * Clean up the timeout when the service is destroyed
   */
  destroy(): void {
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
  }
}
